import { useState, useEffect, useMemo } from "react";
import { isValid } from 'date-fns';
import { Interview } from "@/types/interview";
import { useAiChat } from "@/hooks/interview/useAiChat";
import { modelService } from "@/services/ai/modelService";
import { countWords } from "@/hooks/interview/useListeningTime";
import { TextToSpeechService } from "@/services/speech/TextToSpeechService";

export const useInterviewCard = (props: Interview & { isReversed?: boolean; articleNumber?: number }) => {
  const {
    id,
    answer,
    aiSummary,
    aiResponse,
    intervieweeName,
    photoUrl,
    timestamp,
    summary_status,
    question,
    context,
    title,
    company
  } = props;

  const [isExpanded, setIsExpanded] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [renderKey, setRenderKey] = useState(0);
  const [audioDuration, setAudioDuration] = useState<number>(0);
  const { aiChat, handleAiQuestion, handleAiResponse } = useAiChat();
  const wordCount = countWords(answer);
  
  // Force a re-render after a short delay to ensure images are loaded properly
  useEffect(() => {
    const timer = setTimeout(() => {
      setRenderKey(Date.now());
    }, 1500);
    
    return () => clearTimeout(timer);
  }, []);

  // Process aiSummary for consistent format - MUST be defined before being used
  const processedSummary = useMemo(() => {
    if (!aiSummary) return [];
    
    if (typeof aiSummary === 'string') {
      if (aiSummary.trim().length === 0) return [];
      
      try {
        // Try to parse the string as JSON in case it's a stringified array
        const parsedSummary = JSON.parse(aiSummary);
        if (Array.isArray(parsedSummary)) {
          return parsedSummary.filter(item => !!item && item.trim().length > 0);
        }
      } catch (e) {
        // Not valid JSON, continue with normal string processing
      }
      
      // If string contains line breaks or bullet points, convert to array
      if (aiSummary.includes('\n') || aiSummary.includes('•') || aiSummary.includes('-')) {
        return aiSummary
          .split(/\n|•|-/)
          .map(point => point.trim())
          .filter(Boolean);
      }
      
      // Single string with content, make it an array with one item
      return [aiSummary];
    }
    
    if (Array.isArray(aiSummary)) {
      // Ensure all array items are valid strings
      return aiSummary
        .filter(item => item !== null && item !== undefined)
        .map(item => String(item))
        .filter(point => point.trim().length > 0);
    }
    
    return [];
  }, [aiSummary]);

  // Get audio duration from TTS - Now processedSummary is available
  useEffect(() => {
    const getAudioDuration = async () => {
      try {
        const textToSpeak = TextToSpeechService.formatTextForSpeech({
          text: '',
          context,
          question,
          answer,
          intervieweeName,
          title,
          company,
          aiSummary: Array.isArray(processedSummary) 
            ? processedSummary.join('\n') 
            : typeof processedSummary === 'string' ? processedSummary : '',
          aiResponse
        });

        // Only get duration estimation, don't pre-generate (publishInterviewService already does this)
        // Use estimated duration based on text length instead of generating audio
        const estimatedDuration = Math.ceil(textToSpeak.length / 12); // ~12 chars per second
        setAudioDuration(estimatedDuration);
        console.log(`Estimated TTS duration for ${intervieweeName}: ${estimatedDuration}s (avoiding duplicate generation)`);
      
      } catch (error) {
        console.error('Failed to get audio duration:', error);
        // Keep default duration of 0, will fallback to word count
      }
    };

    // Only generate audio duration if we have the necessary content
    if (answer && question) {
      // Add small delay to prevent too many simultaneous requests
      const timer = setTimeout(() => {
        getAudioDuration();
      }, Math.random() * 2000); // Random delay between 0-2 seconds
      
      return () => clearTimeout(timer);
    }
  }, [answer, question, context, intervieweeName, title, company, processedSummary, aiResponse]);

  useEffect(() => {
    // Check if we need to generate a summary
    const needsSummary = 
      // Either no summary exists
      (!aiSummary || 
       // Or it's an empty string
       (typeof aiSummary === 'string' && aiSummary.trim() === '') ||
       // Or it's an empty array
       (Array.isArray(aiSummary) && aiSummary.length === 0)) &&
      // And the status is pending (or stuck generating for too long)
      (summary_status === 'pending' || summary_status === 'generating') &&
      // And we have an interview ID
      !!id;

    if (needsSummary) {
      console.log(`[InterviewCard] Generating summary for interview ${id} - current status: ${summary_status}`);
      // Don't await - let it run in background to avoid blocking UI
      modelService.generateInterviewSummary(id).catch(error => {
        console.error('[InterviewCard] Background summary generation failed:', error);
        // Also log to console for debugging
        console.error('[InterviewCard] Full error details:', {
          interviewId: id,
          currentStatus: summary_status,
          errorMessage: error.message || 'Unknown error',
          errorStack: error.stack
        });
      });
    }
  }, [id, aiSummary, summary_status]);

  useEffect(() => {
    // Preload image to ensure it's cached
    if (photoUrl) {
      const img = new Image();
      img.src = photoUrl;
      img.onload = () => {
        setImageLoaded(true);
      };
    }
  }, [photoUrl]);

  // Handle timestamp formatting - ensure we have a valid date
  let parsedDate;
  try {
    if (timestamp) {
      parsedDate = new Date(timestamp);
      if (!isValid(parsedDate)) {
        console.error(`Invalid timestamp for ${intervieweeName}: ${timestamp}`);
        parsedDate = new Date();
      }
    } else {
      parsedDate = new Date();
    }
  } catch (e) {
    console.error(`Error parsing timestamp for ${intervieweeName}: ${timestamp}`, e);
    parsedDate = new Date();
  }

  const validTimestamp = parsedDate.toISOString();

  const calculateReadingTime = () => {
    // If we have actual audio duration, use that (convert to minutes for display)
    if (audioDuration && audioDuration > 0) {
      return Math.ceil(audioDuration / 60);
    }
    
    // Fallback to word count estimation
    const wordsPerMinute = 200;
    
    // Handle different aiSummary formats for word count
    const summaryText = Array.isArray(processedSummary) 
      ? processedSummary.join(' ') 
      : typeof processedSummary === 'string' ? processedSummary : '';
    
    const totalWords = countWords([answer, summaryText, aiResponse].join(' '));
    return Math.ceil(totalWords / wordsPerMinute);
  };

  const readingTime = calculateReadingTime();

  return {
    isExpanded,
    setIsExpanded,
    aiChat,
    handleAiQuestion,
    handleAiResponse,
    wordCount,
    renderKey,
    validTimestamp,
    readingTime,
    audioDuration,
    processedSummary
  };
};
